import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import Inicio from "./pages/inicio/inicio";
import Classico from "./pages/classico/classico";
import Login from "./pages/login/login";
import SignUp from "./pages/sign-up/sign-up";
import Acervo from "./pages/meus-quadrinhos/acervo";
import NovoQuadrinho from "./pages/meus-quadrinhos/novo-quadrinho";
import NaoEncontrado from "./pages/nao-encontrado/nao-encontrado";
import EditarQuadrinho from "./pages/meus-quadrinhos/editar-quadrinho";
import VerQuadrinho from "./pages/meus-quadrinhos/ver-quadrinho";
import PublicarQuadrinho from "./publicar/publicar-quadrinho";
import MinhaConta from "./pages/minha-conta/minha-conta";
import { useGlobalState } from "./global-state-context";
import ResetPassword from "./pages/reset-password/reset-password";
import Verificar from "./pages/verificar/verificar";
import Biblioteca from "./pages/biblioteca/biblioteca";
import LerHistoria from "./pages/biblioteca/ler-historia";
import Institucional from "./pages/institucional/institucional";
import MotoresDemo from "./pages/motores/motores-demo";
import Roteirizar from "./pages/roteirizar/roteirizar";
import BancadaDaJanela from "./dev/bancada-da-janela";
import Legal from "./pages/legal/legal";
import { RodapeGlobal } from "./components/rodape";
import { ProvedorDeTema } from "./tema/tema-context";
import PainelDeRegras from "./pages/admin/regras";
import ModeracaoDeInstituicoes from "./pages/admin/instituicoes";
import PaginaDoConvite from "./pages/convite/convite";

function App() {
  const globalState = useGlobalState();

  if (!globalState) return <></>;

  return (
    // O provedor de tema fica FORA do roteador de propósito: o tema vale para a página inteira,
    // inclusive para a barra de navegação, e não deve reiniciar a cada troca de rota.
    <ProvedorDeTema>
      <BrowserRouter>
        <Routes>
          {/* A ROTA PRINCIPAL É A MODERNA. A antiga não foi apagada: mudou de endereço.
              O pedido do operador, na letra: "as rotas principais são as rotas modernizadas",
              e a clássica vira "uma opção de modo clássico". São rotas diferentes que fazem a
              mesma coisa, não um tema pintado por cima do mesmo layout. */}
          <Route element={<Inicio />} path="/" />
          <Route element={<Classico />} path="/classico" />

          {/* T1.18 e T1.36 do pedido: estas três eram âncoras da home (`/#quem-somos`), não
              páginas. Agora cada uma tem endereço próprio, título de aba próprio e volta pelo
              botão "voltar" do navegador. */}
          <Route element={<Institucional secao="quem-somos" />} path="/quem-somos" />
          <Route element={<Institucional secao="objetivos" />} path="/objetivos" />
          <Route element={<Institucional secao="parceiros" />} path="/parceiros" />

          {/* A biblioteca é pública: distribuir é o verbo do pedido, e exigir login para ver o
              que a escola publicou esvaziaria a prateleira. */}
          <Route element={<Biblioteca />} path="/biblioteca" />
          {/* T2.21: "quando eu clico na biblioteca, tem essa parte criada, primeiro quadrinho, e
              ele me leva para a primeira pagina de todo jeito."

              A causa, medida em 12/09: `biblioteca.tsx:243` navegava para `/biblioteca/{id}` e
              ESTA ROTA NAO EXISTIA. Clicar num item do catalogo caia no vazio, porque este
              roteador nao tem rota curinga. A prateleira estava pronta e provada, ligada a um
              endereco que ninguem havia criado. */}
          <Route element={<LerHistoria />} path="/biblioteca/:id" />

          {/* Item 4 do pedido: os motores de canvas, lado a lado. Existe para que a
              afirmação "o formato suporta quatro motores" seja VERIFICÁVEL na tela, e
              não só no meu texto. */}
          <Route element={<MotoresDemo />} path="/motores" />
          {/* T25 · só em desenvolvimento: a janela de gerar com uma conta de exemplo, para medir o layout. */}
          {process.env.NODE_ENV === "development" && <Route element={<BancadaDaJanela />} path="/dev/gerar" />}

          {/* Item 8 do pedido: "gere as páginas do quadrinho a partir de um único prompt".
              O backend tinha `POST /roteirizar` e nenhuma tela o chamava — tem porta, não
              tinha botão. A rota é pública porque escrever uma história não precisa de
              conta; salvar precisa, e é aí que o login entra. */}
          <Route element={<Roteirizar />} path="/roteirizar" />

          <Route element={<Login />} path="/login" />
          <Route element={<SignUp isEditing={false} />} path="/cadastro" />
          {/* ── MINHA CONTA ABSORVEU CONFIGURAÇÕES (PL-T2.12) ─────────────────────────────
              T2.101: "minha conta é uma coisa só (...) botar uma divisão de configurações SE
              TIVER ALGO que precisa colocar. Mas no momento não tem nada."

              `/minha-conta` deixou de ser o FORMULÁRIO e passou a ser a ÁREA: ver os dados,
              editá-los e sair. O formulário ganhou endereço próprio embaixo dela, que é a mesma
              separação que o T2.103 descreve — "tem essa parte de editar meus dados, que é uma
              outra estrutura". */}
          <Route element={<MinhaConta />} path="/minha-conta" />
          <Route element={<SignUp isEditing={true} />} path="/minha-conta/editar" />
          <Route element={<ResetPassword />} path="/auth/action" />
          {/* Ticket 0055 — o link do e-mail de confirmação abre aqui, no cartão do tema quadrinho. */}
          <Route element={<Verificar />} path="/verificar" />
          {/* ── O PAINEL DAS REGRAS (T17) ────────────────────────────────────────────────
              "todas as regras do hqlab devem ser possiveis editar elas (como restrição de
              palavras ou fluxos) num painel onde um perfil com autorização administrativa
              consiga mexer."

              A ROTA É ABERTA DE PROPÓSITO, e a tela é que responde "você não está na lista".
              Esconder a rota não protegeria nada — quem souber o endereço chega nela de
              qualquer jeito —, e a trava de verdade está no banco: `regras/` só aceita escrita
              de quem existe em `administradores/{uid}`, uma coleção que cliente nenhum escreve.
              Uma tela que se esconde sem regra atrás é decoração; a regra sem a tela daria um
              formulário que falha ao salvar. As duas existem, e só uma é segurança. */}
          <Route element={<PainelDeRegras />} path="/admin/regras" />
          {/* Regras de 06/10 (instituicoes-ensino): o admin geral ativa a escola pelo 1º educador
              (caso A), e o link de convite entra já aprovado (caso E). Mesma lógica de rota aberta. */}
          <Route element={<ModeracaoDeInstituicoes />} path="/admin/instituicoes" />
          <Route element={<PaginaDoConvite />} path="/convite/:codigo" />
          {/* ── O ACERVO PESSOAL, EM QUATRO ROTAS ────────────────────────────────────────
              T2.45 do pedido: "o fato de eu ter apenas uma conta dashboard para tudo complica.
              Tem que ser rotas para cada um dessa questao toda ali para tornar mais funcional."

              Era UMA rota com uma maquina de tres estados dentro (`currentComic` indefinido,
              nulo ou um Comic). Seis pontos do T2 sao sintomas disso — o nome da rota (T2.40),
              o acervo so alcancavel por "continuar editando" (T2.41), "voltar" perdendo tudo
              (T2.44), e a falta de um jeito de VER sem editar (T2.43).

              `rascunho` e `novo` vem ANTES de `:id` na leitura humana, e o React Router ja
              prefere segmento estatico a dinamico — as duas nunca sao confundidas com um id. */}
          <Route element={<Acervo />} path="/meus-quadrinhos" />
          <Route element={<NovoQuadrinho />} path="/meus-quadrinhos/novo" />
          <Route element={<EditarQuadrinho />} path="/meus-quadrinhos/rascunho" />
          <Route element={<VerQuadrinho />} path="/meus-quadrinhos/:id" />
          <Route element={<EditarQuadrinho />} path="/meus-quadrinhos/:id/editar" />
          {/* T2.53: "se eu coloquei como publico os meus quadrinhos, eles deveriam aparecer
              aqui." Medido em 12/09: NADA no produto escrevia na colecao `biblioteca` — a
              leitura estava pronta e provada, e o ATO de publicar nao existia. */}
          <Route element={<PublicarQuadrinho />} path="/meus-quadrinhos/:id/publicar" />

          {/* O endereco antigo continua funcionando. Ele esta em historico de navegador, em
              favorito e possivelmente em link que alguem mandou — e uma rota que some leva a
              uma pagina em branco, porque este roteador nao tem rota curinga. */}
          <Route element={<Navigate replace to="/meus-quadrinhos" />} path="/dashboard" />
          {/* T2.105, na letra: "configuração para mim tá nada a ver. TANTO O TERMO QUANTO ESSA
              PÁGINA EXISTIR, porque não tem, ela não tem propósito."

              A página deixou de existir; o ENDEREÇO redireciona. Pelo mesmo motivo do
              `/dashboard`: este roteador não tem rota curinga, e endereço que some vira página em
              branco para quem o tem no histórico. */}
          <Route element={<Navigate replace to="/minha-conta" />} path="/configuracoes" />

          {/* ── AS TRÊS PÁGINAS LEGAIS ────────────────────────────────────────────────────
              T2.12 do pedido, na letra: "se eu quiser checar LGPD disso aqui, não tenho como
              checar". Medido em 2026-09-12: não havia nenhuma das três em rota nenhuma. O texto
              vem do modelo que ele mandou usar (T2.14) — o `educahubplay/app/legal/` —, com os
              FATOS trocados pelos do HQ-Lab, inclusive onde eles são lacuna. */}
          <Route element={<Legal secao="lgpd" />} path="/lgpd" />
          <Route element={<Legal secao="privacidade" />} path="/privacidade" />
          <Route element={<Legal secao="termos" />} path="/termos" />

          {/* ── A ROTA CURINGA, QUE FALTAVA ────────────────────────────────────────────────
              Medido em 2026-09-12 com o navegador, nas 25 rotas declaradas acima: todas as 25
              tinham a barra inteira e contraste de no mínimo 6,4:1. Uma única situação dava tela
              sem cabeçalho — um endereço que não casa com rota nenhuma, e aí a página inteira
              virava o RODAPÉ e mais nada.

              A causa é uma assimetria minha: o `RodapeGlobal` foi içado para fora do `<Routes>`,
              e o `<Nav />` não — cada página monta o seu. Sem rota que case, nenhuma página
              monta, nenhum cabeçalho existe, e sobra só o elemento içado.

              Dois comentários acima já diziam "este roteador não tem rota curinga" e resolviam
              UM endereço cada (`/dashboard`, `/configuracoes`). Esta linha resolve o caso geral,
              que é qualquer erro de digitação e qualquer link velho. Ela fica por ÚLTIMO: `path="*"`
              casa com tudo. */}
          <Route element={<NaoEncontrado />} path="*" />
        </Routes>

        {/* O RODAPÉ, UMA VEZ, PARA O PRODUTO INTEIRO.
            Ele ficava dentro do `inicio.tsx` como markup, e por isso existia em UMA tela de
            catorze (T2.11, T2.12). Montado aqui, rota nova ganha rodapé por padrão — e é isso
            que permite ao `PL-T2.4` descer o institucional para baixo sem que ele suma das
            outras treze telas (T2.29: "tem um botãozinho, ela consegue"). */}
        <RodapeGlobal />
      </BrowserRouter>
    </ProvedorDeTema>
  );
}

export default App;
